export interface CardDimensions {
  cardWidth: string;
  cardBorderRadius: string;
  borderWidth: string;
  cardMargin: string;

  // Fonts
  playerNameFontSize: string;
  playerStatFontSize: string;
  playerDescriptionHeaderFontSize: string;
  playerDescriptionBodyFontSize: string;

  // StatsFrame specific calculations
  statsFrameMargin: string;
  statsFrameWidth: string;
  statsFrameInnerHeight: string;

  // PlayingDetails specific calculations
  attributeBubbleDiameter: string;
  playingDetailsRightOffset: string;
  playingDetailsTopOffset: string;
  playingDetailsStackSpacing: string;
  playingDetailsNationIconWidth: string;
  playingDetailsNationHeroIconFontSize: string;
  playingDetailsRaceIconWidth: string;
  heroIconOutlineWidth: string;

  // PlayingStats & Bubbles specific calculations
  playingStatsLeftOffset: string;
  playingStatsStackSpacing: string;
  attributeBubbleBorderWidth: string;

  // New properties for remaining components
  playerNameMarginBottom: string; // Margin below player name inside StatsFrame
  descriptionContainerMarginTop: string; // Margin above description container inside StatsFrame
  descriptionCardMargin: string; // Margin for description content cards (Role/Race/Nation)
  descriptionHeaderIconWidth: string; // Width for icons in description headers (Race/Nation)
  descriptionBodyIconHeight: string; // Height for icons in description body (Nation favoured enemy)
  traitsListSpacing: string; // Spacing for PlayerTraitsList stack
  traitCardPadding: string; // Padding for cards within PlayerTraitsList
  artworkAvatarBorderRadius: string; // Specific border-radius for the avatar artwork container
  playerNameBackgroundWidth: string; // Width of the PlayerName background card
  playerNameBackgroundPadding: string; // Padding of the PlayerName background card

  // Flags
  showName?: boolean;
  showAttributes?: boolean;
  showDetails: boolean;
  showDescription?: boolean;
  showRarityGlow?: boolean;
  switchDetails?: boolean;

  defaultInLine?: boolean;
}

/**
 * Helper function to generate a CSS value string.
 * Calculates vwValue based on vhValue and aspectRatio.
 * Returns either a fixed 'vh' value or a 'min(vh, vw)' value
 * depending on the ignoreVw flag. Also handles zero/negative inputs.
 */
const getResponsiveCssValue = (
  vhValue: number,
  aspectRatio: number,
  ignoreVw?: boolean
): string => {
  const formatNumber = (num: number) => parseFloat(num.toFixed(2));
  if (vhValue === 0) {
    return '0px';
  }
  if (ignoreVw) {
    return `${formatNumber(vhValue)}vh`;
  }
  const vwValue = vhValue / aspectRatio;
  if (vhValue < 0) {
    return `max(${formatNumber(vhValue)}vh, ${formatNumber(vwValue)}vw)`;
  } else {
    return `min(${formatNumber(vhValue)}vh, ${formatNumber(vwValue)}vw)`;
  }
};

export const createCardDimensions = (
  baseWidthVh: number,
  baseFontSizeVh: number,
  showName: boolean,
  showBorder: boolean,
  showAttributes: boolean,
  showDetails: boolean,
  showDescription: boolean,
  ignoreVw: boolean,
  switchDetails: boolean
): CardDimensions => {
  const aspectRatio = 0.5;

  // Base values
  const widthVh = baseWidthVh;
  const fontVh = baseFontSizeVh;

  // Intermediate dimension values (vh only)
  const radiusVh = widthVh * 0.12;
  const borderMultiplier = showBorder ? 0.02 : 0;
  const borderVh = widthVh * borderMultiplier;
  const nameFontVh = fontVh * 1.05;
  const statFontVh = fontVh * 0.5;
  const descHeaderFontVh = fontVh * 0.4;
  const descBodyFontVh = fontVh * 0.25;

  // StatsFrame intermediate values (vh only)
  const statsFrameMarginVh = borderVh * 2;
  const statsFrameWidthVh = widthVh - borderVh * 6;
  const statsFrameInnerHeightVh = widthVh - borderVh * 3;

  const marginVh = statFontVh * 2 - statsFrameMarginVh; // should also be - borderVh, but need to account for the fact that stat bubbles can grow substantially

  // PlayingDetails intermediate values (vh only)
  const attributeBubbleDiameterVh = statFontVh * 3;
  const playingDetailsRightOffsetVh = borderVh * -0.25;
  const playingDetailsTopOffsetVh = attributeBubbleDiameterVh * -0.5 + borderVh; // Corrected
  const detailsStackSpacingVh = statFontVh * 0.2;
  const playingDetailsIconWidthVh = statFontVh * 2;
  const playingDetailsNationHeroIconFontSizeVh = statFontVh * 2;

  // PlayingStats & Bubbles intermediate values (vh only)
  const playingStatsLeftOffsetVh = borderVh * -0.25;
  const statsStackSpacingVh = statFontVh * 0.2;
  const attributeBubbleBorderWidthVh = borderVh * 0.5;

  // Intermediate values for remaining components (vh only)
  const playerNameMarginBottomVh = nameFontVh * -0.7;
  const descriptionContainerMarginTopVh = nameFontVh;
  const descriptionCardMarginVh = borderVh * 2; // Direct value
  const descriptionHeaderIconWidthVh = descHeaderFontVh; // Direct value
  const descriptionBodyIconHeightVh = descBodyFontVh; // Direct value
  const traitsListSpacingVh = descBodyFontVh * 0.5;
  const traitCardPaddingVh = descBodyFontVh * 0.5;
  const playerNameBackgroundWidthVh = widthVh + statFontVh * 2;
  const playerNameBackgroundPaddingVh = nameFontVh * 0.25;

  // --- Generate Final CSS Strings ---
  const cardWidthString = getResponsiveCssValue(widthVh, aspectRatio, ignoreVw);
  const cardBorderRadiusString = getResponsiveCssValue(
    radiusVh,
    aspectRatio,
    ignoreVw
  );
  const borderWidthString = getResponsiveCssValue(
    borderVh,
    aspectRatio,
    ignoreVw
  );
  const playerStatFontSizeString = getResponsiveCssValue(
    statFontVh,
    aspectRatio,
    ignoreVw
  ); // Base for player name width calc
  const heroIconOutlineWidth = getResponsiveCssValue(
    statFontVh * 0.1,
    aspectRatio,
    true
  ); //ignore vw as css min does not work in text shadow prop
  const playerNameFontSizeString = getResponsiveCssValue(
    nameFontVh,
    aspectRatio,
    ignoreVw
  );
  const descBodyFontSizeString = getResponsiveCssValue(
    descBodyFontVh,
    aspectRatio,
    ignoreVw
  );
  const descHeaderFontSizeString = getResponsiveCssValue(
    descHeaderFontVh,
    aspectRatio,
    ignoreVw
  );

  // Special string constructions
  const artworkAvatarBorderRadiusString = `${cardBorderRadiusString} ${cardBorderRadiusString} 0 0`;

  return {
    // Card Base
    cardWidth: cardWidthString,
    cardBorderRadius: cardBorderRadiusString,
    borderWidth: borderWidthString,
    cardMargin: getResponsiveCssValue(marginVh, aspectRatio, ignoreVw),
    // Fonts
    playerNameFontSize: playerNameFontSizeString,
    playerStatFontSize: playerStatFontSizeString,
    heroIconOutlineWidth: heroIconOutlineWidth,
    playerDescriptionHeaderFontSize: descHeaderFontSizeString,
    playerDescriptionBodyFontSize: descBodyFontSizeString,
    // StatsFrame
    statsFrameMargin: getResponsiveCssValue(
      statsFrameMarginVh,
      aspectRatio,
      ignoreVw
    ),
    statsFrameWidth: getResponsiveCssValue(
      statsFrameWidthVh,
      aspectRatio,
      ignoreVw
    ),
    statsFrameInnerHeight: getResponsiveCssValue(
      statsFrameInnerHeightVh,
      aspectRatio,
      ignoreVw
    ),
    // PlayingDetails
    attributeBubbleDiameter: getResponsiveCssValue(
      attributeBubbleDiameterVh,
      aspectRatio,
      ignoreVw
    ),
    playingDetailsRightOffset: getResponsiveCssValue(
      playingDetailsRightOffsetVh,
      aspectRatio,
      ignoreVw
    ),
    playingDetailsTopOffset: getResponsiveCssValue(
      playingDetailsTopOffsetVh,
      aspectRatio,
      ignoreVw
    ),
    playingDetailsStackSpacing: getResponsiveCssValue(
      detailsStackSpacingVh,
      aspectRatio,
      ignoreVw
    ),
    playingDetailsNationIconWidth: getResponsiveCssValue(
      playingDetailsIconWidthVh,
      aspectRatio,
      ignoreVw
    ),
    playingDetailsNationHeroIconFontSize: getResponsiveCssValue(
      playingDetailsNationHeroIconFontSizeVh,
      aspectRatio,
      ignoreVw
    ),
    playingDetailsRaceIconWidth: getResponsiveCssValue(
      playingDetailsIconWidthVh,
      aspectRatio,
      ignoreVw
    ),
    // PlayingStats & Bubbles
    playingStatsLeftOffset: getResponsiveCssValue(
      playingStatsLeftOffsetVh,
      aspectRatio,
      ignoreVw
    ),
    playingStatsStackSpacing: getResponsiveCssValue(
      statsStackSpacingVh,
      aspectRatio,
      ignoreVw
    ),
    attributeBubbleBorderWidth: getResponsiveCssValue(
      attributeBubbleBorderWidthVh,
      aspectRatio,
      ignoreVw
    ),
    // Remaining Components
    playerNameMarginBottom: getResponsiveCssValue(
      playerNameMarginBottomVh,
      aspectRatio,
      ignoreVw
    ),
    descriptionContainerMarginTop: getResponsiveCssValue(
      descriptionContainerMarginTopVh,
      aspectRatio,
      ignoreVw
    ),
    descriptionCardMargin: getResponsiveCssValue(
      descriptionCardMarginVh,
      aspectRatio,
      ignoreVw
    ),
    descriptionHeaderIconWidth: getResponsiveCssValue(
      descriptionHeaderIconWidthVh,
      aspectRatio,
      ignoreVw
    ),
    descriptionBodyIconHeight: getResponsiveCssValue(
      descriptionBodyIconHeightVh,
      aspectRatio,
      ignoreVw
    ),
    traitsListSpacing: getResponsiveCssValue(
      traitsListSpacingVh,
      aspectRatio,
      ignoreVw
    ),
    traitCardPadding: getResponsiveCssValue(
      traitCardPaddingVh,
      aspectRatio,
      ignoreVw
    ),
    artworkAvatarBorderRadius: artworkAvatarBorderRadiusString, // Use pre-formatted string
    playerNameBackgroundWidth: getResponsiveCssValue(
      playerNameBackgroundWidthVh,
      aspectRatio,
      ignoreVw
    ),
    playerNameBackgroundPadding: getResponsiveCssValue(
      playerNameBackgroundPaddingVh,
      aspectRatio,
      ignoreVw
    ),
    // Flags
    showAttributes: showAttributes,
    showName: showName,
    showDetails: showDetails,
    showDescription: showDescription,
    showRarityGlow: true,
    switchDetails: switchDetails,
  };
};

// --- Predefined sizes remain unchanged ---
export const PitchDimensions = createCardDimensions(
  5, // baseWidthVh
  1.2, // baseFontSizeVh
  true, // showName
  false, // showBorder
  true, // showAttributes
  true, // showDetails
  false, // showDescription
  true, // ignoreVw,
  false // switchDetails
);

export const SmallCard: CardDimensions = createCardDimensions(
  10, // baseWidthVh
  1.8, // baseFontSizeVh
  true, // showName
  false, // showBorder
  true, // showAttributes
  true, // showDetails
  false, // showDescription
  true, // ignoreVw
  false // switchDetails
);

export const SmallSelectedCard: CardDimensions = {
  ...SmallCard,
  borderWidth: `calc(${SmallCard.cardWidth} * 0.02)`,
  cardWidth: `calc(${SmallCard.cardWidth} + (${SmallCard.playerStatFontSize} * 2)} * 1.05)`,
};

export const MediumCard: CardDimensions = createCardDimensions(
  25, // baseWidthVh
  2.5, // baseFontSizeVh
  true, // showName
  true, // showBorder
  true, // showAttributes
  true, // showDetails
  true, // showDescription
  false, // ignoreVw
  true // switchDetails
);

export const LargeCard: CardDimensions = createCardDimensions(
  40, // baseWidthVh
  3.6, // baseFontSizeVh
  true, // showName
  true, // showBorder
  true, // showAttributes
  true, // showDetails
  true, // showDescription
  false, // ignoreVw
  true // switchDetails
);

export const Printable: CardDimensions = createCardDimensions(
  35, // baseWidthVh
  4, // baseFontSizeVh
  true, // showName
  true, // showBorder
  true, // showAttributes
  true, // showDetails
  true, // showDescription
  false, // ignoreVw
  true // switchDetails
);

export const InLine: CardDimensions = {
  ...createCardDimensions(
    3, // baseWidthVh
    1.6, // baseFontSizeVh
    true, // showName
    false, // showBorder
    true, // showAttributes
    true, // showDetails
    false, // showDescription
    true, // ignoreVw,
    false // switchDetails
  ),
  defaultInLine: true,
};

export const InLineNoName: CardDimensions = {
  ...InLine,
  showName: false,
};
